/**
 * `usePathname()` is not the same string on the server and the client.
 *
 * `proxy.ts` rewrites several public URLs onto prerendered *variant* routes
 * that carry an extra `v/{code}` segment pair — the precomputed flag bundle for
 * that request (homepage, `/tests`, `/tests/{slug}`, `/biomarkers/{slug}`,
 * `/what-we-detect/{slug}`, `/go/insurancemri`, …):
 *
 *     public   /ch/en/tests/advanced-blood-test
 *     internal /ch/en/tests/v/{code}/advanced-blood-test   ← what SSR renders
 *
 * During SSR `usePathname()` reports the **rewritten** path, because that is
 * the route being rendered. On the client it reports the **browser** URL, which
 * is the public one — the rewrite is invisible to the address bar. So any
 * pathname check that isn't rewrite-tolerant answers differently on the two
 * sides, and if it feeds render output that is a guaranteed hydration mismatch.
 *
 * That is exactly how MARKETING-SITE-N happened: `page-shell.tsx` decided the
 * hero-overlay navbar with an exact `pathSegments.length === 4` test, which the
 * 6-segment rewritten path fails. The server rendered the opaque navbar and the
 * client the transparent one — React #418 on every test-detail page, plus a
 * visible navbar flash. Proof it is the rewrite and not the shape: the
 * same-shaped but deliberately un-rewritten `/ch/en/tests/lpa` hydrates clean,
 * while `/ch/en/tests/advanced-blood-test` fails 100% of the time.
 *
 * Normalising here rather than teaching each call site about `v/{code}` keeps
 * new pathname checks correct by default — the variant segments are a routing
 * implementation detail that no feature code should have to know about.
 */

/**
 * Strip the `v/{code}` variant pair that `proxy.ts` injects, returning the
 * public path the user's address bar shows. Safe to call on an already-public
 * path — it is a no-op there.
 */
export function publicPathname(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean)
  // The pair never appears before the `/{country}/{locale}` prefix, and `v`
  // must be followed by the code itself — a trailing `/v` is a real route
  // segment, not a variant marker.
  const i = segments.findIndex(
    (segment, index) =>
      index >= 2 && segment === "v" && index < segments.length - 1
  )
  if (i === -1) return pathname
  const stripped = [...segments.slice(0, i), ...segments.slice(i + 2)]
  const trailingSlash = pathname.endsWith("/") && pathname !== "/"
  return `/${stripped.join("/")}${trailingSlash ? "/" : ""}`
}
